/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
import React, {
 useEffect,
 useCallback,
 useState,
} from 'react'
// import {toast} from 'react-toastify'
import {Button} from 'helpers/ui-components/Button'
import {useTranslation} from 'react-i18next'
import {KTSVG} from 'helpers'
import {fetchUserList} from 'utils/api/auth/userManagementService'
import {IUser, IUserAction} from 'models/UserManagement'
import {KTFilterBox} from 'helpers/ui-components/KTFilterBox'
import {KTSearchBox} from 'helpers/ui-components/KTSearchBox'
import {fetchFilterConfig} from 'utils/api/auth/commonService'
import {createFilterPayload} from 'utils/helpers/genericFilterForm'
import {
 ColumnProps,
 IFormField,
 IRejectionReason,
} from 'models/Common'
import KTTable from 'helpers/ui-components/KTTable'
import {DefaultApiResponse} from 'models/ApiResponse'
import {formatDateForDisplay} from 'utils/helper'
import {Link} from 'react-router-dom'

type IProps = {
 accessDetails: IFormField
 userId: string
 loggedInUserId: number | undefined
 setLoader: (newState: boolean) => void
 setActionDetails: (newState: IUserAction | null) => void
}

const RejectedUser = (props: IProps) => {
 const {t} = useTranslation()
 const [currentPage, setCurrentPage] = useState(1)
 const [userListRes, setUserListRes] =
  useState<DefaultApiResponse>()
 const [userList, setUserList] = useState<IUser[]>([])
 const [tableColList, setTableColList] = useState<
  Array<ColumnProps<IUser>>
 >([])
 const [itemsPerPage, setItemsPerPage] = useState(25)
 const [searchTerm, setSearchTerm] = useState('')
 const [filterConfig, setFilterConfig] = useState<
  IFormField[]
 >([])
 const [filterFormData, setFilterFormData] =
  useState<IFormField | null>({})
 const [filterCount, setFilterCount] = useState(0)
 const userDetailsAccess =
  props.accessDetails?.userDetails?.section

 const getFilterConfig = useCallback(async () => {
  try {
   const res = await fetchFilterConfig({
    filter_type: 'USER',
   })
   if (res) {
    setFilterConfig(res?.applicableFilters ?? [])
   }
  } catch (error) {
   setFilterConfig([])
  }
 }, [])

 useEffect(() => {
  void getFilterConfig()
 }, [getFilterConfig])

 const getUserList = async (
  pageNo = 1,
  pageSize = 10,
  signal?: AbortSignal
 ) => {
  props.setLoader(true)
  try {
   let query: IFormField = {}
   let appliedFilters = 0
   if (filterConfig?.length) {
    const payload = await createFilterPayload(
     filterConfig,
     filterFormData
    )
    const {totalAppliedFilters, ...restPayload} = payload
    appliedFilters =
     typeof totalAppliedFilters === 'number'
      ? totalAppliedFilters
      : Number(totalAppliedFilters ?? 0)
    query = restPayload as IFormField
   }
   const trimmedSearch = searchTerm.trim()
   if (trimmedSearch) {
    query = {
     ...query,
     search: trimmedSearch,
    }
   }
   const res: DefaultApiResponse | null =
    await fetchUserList(
     'rejected',
     pageNo,
     pageSize,
     Object.keys(query).length ? query : undefined,
     signal
    )
   if (res) {
    setUserListRes(res)
    setUserList(res.results ?? [])
   }
   setFilterCount(appliedFilters)
  } finally {
   props.setLoader(false)
  }
 }
 const handlePageChange = async (pageNo: number) => {
  setCurrentPage(pageNo)
  await getUserList(pageNo, itemsPerPage)
 }
 const handleItemsPerPageChange = (pageSize: number) => {
  setItemsPerPage(pageSize)
  setCurrentPage(1)
 }

 const handleActionBtnClick = useCallback(
  (userId: number) => {
   props.setActionDetails({
    userId: userId.toString(),
    actionType: 'rejected_user',
   })
  },
  [props.setActionDetails]
 )

 useEffect(() => {
  const abortController = new AbortController()
  if (currentPage !== 1) {
   setCurrentPage(1)
  }
  void getUserList(1, itemsPerPage, abortController.signal)
  return () => {
   abortController.abort()
  }
 }, [filterFormData, searchTerm, itemsPerPage])

 const renderTableData = useCallback(async () => {
  props.setLoader(true)

  const columns: Array<ColumnProps<IUser>> = [
   {
    key: 'srNo',
    title: t('userManagement.userListTable.srNo'),
    className: 'w-[80px] text-center',
    isSrNo: true,
   },
   {
    key: 'full_name',
    title: t('userManagement.userListTable.name'),
    render: (_, record) => {
     return (
      <button
       type='button'
       disabled={!userDetailsAccess}
       className={`${
        userDetailsAccess && 'text-[#4E83E4] underline'
       } w-full text-left disabled:bg-transparent`}
       onClick={() => {
        if (userDetailsAccess)
         void handleActionBtnClick(record.id)
       }}
      >
       {record.full_name}
      </button>
     )
    },
   },
   {
    key: 'organisation',
    title: t('userManagement.userListTable.firmName'),
   },
   {
    key: 'phone_number',
    title: t('userManagement.userListTable.mobile'),
    className: 'w-[80px]',
   },
   {
    key: 'created_at',
    title: t(
     'userManagement.userListTable.userCreationDate'
    ),
    render: (_, record) => {
     return formatDateForDisplay(record?.created_at)
    },
   },
   {
    key: 'stakeholder',
    title: t('userManagement.userListTable.stakeholder'),
   },
   {
    key: 'category',
    title: t('userManagement.userListTable.category'),
   },
   {
    key: 'user_category',
    title: t('userManagement.userListTable.userCategory'),
   },
   {
    key: 'reason',
    title: t('userManagement.userListTable.rejectedReason'),
    render: (_, record) => {
     return (
      <div className='flex items-center justify-center w-full'></div>
     )
    },
   },
  ]
  setTableColList(columns)
  props.setLoader(false)
 }, [
  handleActionBtnClick,
  userDetailsAccess,
  props.setLoader,
  t,
  userDetailsAccess,
 ])

 useEffect(() => {
  void getUserList(1, itemsPerPage)
 }, [props.userId])

 useEffect(() => {
  if (userList) {
   void renderTableData()
  }
 }, [renderTableData, userList])

 return (
  <>
   <div className='w-full'>
    {/* <div className='flex items-center justify-between text-[#171B1F] bg-[#F5F8FA] text-xl font-bold rounded-lg p-4 mb-6'>
     <div className='flex items-center '>
      <Button
       disabled={false}
       onClick={() => {}}
       color='white'
       className='text-center py-[9px] p-3 bg-white 
                          !rounded-lg text-sm !text-[#4E83E4] font-semibold border-[#E6EBF0]'
      >
       <KTSVG
        path={`/media/icons/filter.svg`}
        svgClassName={`w-4 h-4 text-white mr-2`}
       />
       {t('helperText.filter')}
      </Button>
     </div>
    </div> */}
    <div className='my-6'>
     <div className='grid md:grid-cols-2 items-center gap-4'>
      <KTSearchBox
       label={t('userManagement.rejected.search')}
       placeholder={t(
        'userManagement.rejected.searchPlaceholder'
       )}
       setSearchTerm={setSearchTerm}
      />
      {filterConfig?.length > 0 && (
       <div className='flex gap-4 items-center justify-end'>
        <KTFilterBox
         parentId='rejected_user'
         btnText={t('userManagement.rejected.filters')}
         containerClass='bg-white border-none mt-0 pl-0 justify-end'
         filterConfig={filterConfig}
         filterFormData={filterFormData ?? {}}
         setFilterFormData={setFilterFormData}
         filterCount={filterCount}
         setLoader={props.setLoader}
         showDateRangeBox={false}
        />
       </div>
      )}
     </div>
    </div>
    <div>
     <KTTable
      data={userList}
      columns={tableColList}
      headerClass='bg-[#80868C]'
      headerColClass='!border-0 text-left'
      colClass='text-[#33393D] !text-sm font-normal !border-0 !py-3'
      rowClass='even:bg-[#E6EBF0]'
      tableClass='last-col-sticky-table !border-1 border-b'
      showPagination={true}
      currentPage={currentPage}
      itemsPerPage={itemsPerPage}
      totalRecord={userListRes?.count}
      onPageChange={handlePageChange}
      setItemsPerPage={handleItemsPerPageChange}
     />
    </div>
   </div>
  </>
 )
}

export default RejectedUser
